import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { eventsAPI } from '../services/api'
import { Calendar, MapPin, Users, ArrowLeft, ExternalLink } from 'lucide-react'
import { getImageUrl } from '../utils/imageHelper'

const EventDetail = () => {
  const { id } = useParams()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rsvpSuccess, setRsvpSuccess] = useState(false)

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true)
        const response = await eventsAPI.getById(id)
        // Handle both old format (response.data.data) and new format (response.data)
        const event = response.data?.id ? response.data : response.data?.data;
        setEvent(event)
      } catch (err) {
        console.error('Error fetching event:', err)
        setError('Event not found or failed to load.')
      } finally {
        setLoading(false)
      }
    }

    fetchEvent()
  }, [id])

  const formatDate = (dateString) => {
    const eventDate = new Date(dateString)
    return eventDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatTime = (dateString) => {
    const eventDate = new Date(dateString)
    return eventDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const handleRSVP = () => {
    // Mock RSVP functionality
    setRsvpSuccess(true)
    setTimeout(() => setRsvpSuccess(false), 3000)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-earth-green"></div>
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link to="/events" className="btn-primary">
            Back to Events
          </Link>
        </div>
      </div>
    )
  }

  const isUpcoming = new Date(event.date) > new Date()
  const isFull = event.maxAttendees && event.currentAttendees >= event.maxAttendees

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b">
        <div className="container-custom">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-earth-green">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/events" className="text-gray-600 hover:text-earth-green">Events</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900">{event.title}</span>
          </div>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Header */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center space-x-2 mb-4">
                <span className="bg-earth-green text-white px-3 py-1 rounded-full text-sm font-medium capitalize">
                  {event.category}
                </span>
                {!isUpcoming && (
                  <span className="bg-gray-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Past Event
                  </span>
                )}
                {isFull && isUpcoming && (
                  <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Full
                  </span>
                )}
              </div>

              <h1 className="text-4xl font-bold text-gray-900 mb-4">{event.title}</h1>
              <p className="text-xl text-gray-600">{event.description}</p>
            </div>

            {/* Event Image */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <img
                src={getImageUrl(event.image)}
                alt={event.title}
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>

            {/* Event Description */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">About This Event</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 text-lg leading-relaxed">
                  {event.fullDescription}
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Details */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Event Details</h3>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Calendar className="h-5 w-5 text-earth-green mt-1" />
                  <div>
                    <div className="font-medium text-gray-900">{formatDate(event.date)}</div>
                    <div className="text-sm text-gray-600">{formatTime(event.date)}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-earth-green mt-1" />
                  <div>
                    <div className="font-medium text-gray-900">{event.location.name}</div>
                    <div className="text-sm text-gray-600">
                      {event.location.address}<br />
                      {event.location.city}, {event.location.state} {event.location.zipCode}
                    </div>
                  </div>
                </div>

                {event.maxAttendees && (
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-earth-green mt-1" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {event.currentAttendees} / {event.maxAttendees} attendees
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div
                          className="bg-earth-green h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(event.currentAttendees / event.maxAttendees) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RSVP Section */}
            {isUpcoming && (
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Registration</h3>

                {event.registrationRequired ? (
                  <div className="space-y-4">
                    {isFull ? (
                      <div className="text-center py-4">
                        <p className="text-red-600 font-medium mb-2">This event is full</p>
                        <p className="text-sm text-gray-600">
                          Please check back for future events or contact us to be added to the waitlist.
                        </p>
                      </div>
                    ) : (
                      <>
                        {rsvpSuccess ? (
                          <div className="text-center py-4">
                            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-2" />
                            <p className="text-green-600 font-medium">RSVP Successful!</p>
                            <p className="text-sm text-gray-600">
                              You'll receive a confirmation email shortly.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <p className="text-gray-600 text-sm">
                              Registration is required for this event. Click below to RSVP.
                            </p>

                            {event.registrationUrl ? (
                              <a
                                href={event.registrationUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary w-full flex items-center justify-center group"
                              >
                                Register Online
                                <ExternalLink className="h-4 w-4 ml-2 group-hover:scale-110 transition-transform" />
                              </a>
                            ) : (
                              <button
                                onClick={handleRSVP}
                                className="btn-primary w-full"
                              >
                                RSVP Now
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-600">
                      No registration required. Just show up and join us!
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Contact Info */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions?</h3>
              <p className="text-gray-600 text-sm mb-4">
                Have questions about this event? We're here to help!
              </p>
              <Link
                to="/about#contact"
                className="btn-outline w-full text-center"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <Link
            to="/events"
            className="btn-outline inline-flex items-center group"
          >
            <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Events
          </Link>
        </div>
      </div>
    </div>
  )
}

export default EventDetail
